import {createContext, useState, useEffect, useMemo} from "react"
import uniqueId from "lodash/uniqueId"
import {BannerLink} from "./text/models"

type SearchBannerContextType = {
    hasChevron: boolean
    handleShowChevron: (show: boolean) => void
    isExpanded: boolean
    toggleIsExpanded: () => void
    siteUrl: string
}

export const SB_TYPE = "esi-searchbanner"
export const COPY_TYPE = "Copy"
export const STRIP_TYPE = "Strip"
export const STRIP = "strip"
export const COPY_TEXT = "copy-text"
export const ROUNDELS = "roundels"
export const HEADING = "heading"
export const CHIPS = "chips"
export const INTEL_RECS = "intel-recs"
export const AI_ASSISTANT_ICON_ALT = "Next AI Assistant Icon"

export const SearchBannerContext = createContext<SearchBannerContextType>({} as SearchBannerContextType)

type WindowSize = {
    width: number | undefined
    height: number | undefined
}

export function useWindowSize() {
    const [windowSize, setWindowSize] = useState<WindowSize>({
        width: undefined,
        height: undefined,
    })
    useEffect(() => {
        function handleResize() {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            })
        }
        window.addEventListener("resize", handleResize)
        handleResize()
        return () => window.removeEventListener("resize", handleResize)
    }, [])
    return windowSize
}

/* istanbul ignore next */ // to test this in cypress when we integrate new searchbanners
export function shouldHideReadMore(element: HTMLElement, isExpanded: boolean): boolean {
    const {clientHeight, scrollHeight} = element
    const isTruncated = clientHeight && scrollHeight && scrollHeight > clientHeight
    if (!isExpanded && !isTruncated) {
        return true
    }
    return false
}

// what if the value is == "", should we check for blank and treat it as undefined?
// is empty string ever a valid css property
// on ::before, content: ""
export function removeUnsetProps(obj: {[key: string]: any}) {
    const result = {}
    Object.keys(obj).forEach(key => {
        if (typeof obj[key] === "object" && obj[key] != null) {
            const nestedObj = removeUnsetProps(obj[key])
            if (Object.keys(nestedObj).length > 0) {
                result[key] = nestedObj
            }
        } else if (obj[key] != null) {
            result[key] = obj[key]
        }
    })
    return result
}

export const getHeightAndWidth = imageSize => {
    if (imageSize === undefined || typeof imageSize !== "string") {
        return [undefined, undefined]
    }
    return imageSize.toLowerCase().split("x")
}

export const getText = (type: "Copy" | "Strip", string: string, current: BannerLink) => {
    return type === "Copy" ? current?.text : string.slice(1, -1)
}

export const useUniqueKey = (size: number) => {
    const ids: string[] = []
    for (let ii = 0; ii < size; ii += 1) {
        ids.push(uniqueId(""))
    }
    return useMemo(() => ids, [])
}

export const replaceSiteUrl = (link: string, baseUrl = "") => {
    if (!link) return ""
    return link.replace(/{{SiteUrl}}/g, baseUrl)
}

export const getSearchBannerComponentTestId = (isIntelRecs: boolean, componentName: string): string => {
    return isIntelRecs ? `${INTEL_RECS}-search-banner-${componentName}` : `search-banner-${componentName}`
}
